import React, { useState, Fragment, useEffect, useRef } from "react";

// ui
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Tooltip,
} from "@mui/material";
import { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/system";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AddIcon from "@mui/icons-material/Add";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CancelIcon from "@mui/icons-material/Cancel";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DoDisturbAltIcon from "@mui/icons-material/DoDisturbAlt";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import AddIcCallIcon from "@mui/icons-material/AddIcCall";
import CallIcon from "@mui/icons-material/Call";
import ReplayIcon from "@mui/icons-material/Replay";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

import {
  dataDifine,
  subDataDifine,
  DataColumn,
  TableColumn,
  SubTableColumn,
  EditDataDifine,
  SubDataColumn,
} from "../config/dataInterface";
import { getOneYearList } from "../lib/other";

// api
import { getPresignedGetPicture } from "../api/presignedURL";
import {
  getOptionSelects,
  getOptionSelectsTossUp,
  getOptionSelectsOutside,
} from "../api/optionSelect";
import { getPaymentConfirmExcel } from "../api/paymentConfirmExcel";

// jotai
import { useAtomValue, useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";

import { CONST } from "../config/const";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: "1.5px 1.5px rgba(115, 115, 115, 0.8)",
    fontSize: 11,
  },
}));

interface TableTitleProps {
  serchOpen: boolean;
  aiChatOpen?: boolean;
  pageList: any;
  tableColumn: TableColumn;
  displayNumber: number;
  setDisplayNumber: (displayNumber: number) => void;
  displayNumbers: number[];
  pageNumber: number;
  setPageNumber: (displayNumber: number) => void;
  maxPageNumber: number;
  tableTitle: string;
  getTableData: (edit: boolean) => void;
  totalCount?: number;
}

function TableTitle(props: TableTitleProps) {
  const {
    serchOpen,
    aiChatOpen = false,
    pageList,
    tableColumn,
    displayNumber,
    setDisplayNumber,
    displayNumbers,
    pageNumber,
    setPageNumber,
    maxPageNumber,
    tableTitle,
    getTableData,
    totalCount,
  } = props;

  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  return (
    <Typography
      variant="h6"
      gutterBottom
      component="div"
      position={isSmartPhone ? "fixed" : "static"}
      style={{
        width: "100%",
        top: isSmartPhone ? "80px" : undefined,
        backgroundColor: "white",
        paddingTop: isSmartPhone ? "8px" : undefined,
        paddingBottom: isSmartPhone ? "8px" : undefined,
        zIndex: isSmartPhone ? 1 : undefined,
      }}
    >
      <Box
        className="shippori-antique-regular"
        sx={{
          display: "inline",
          fontSize: "16px",
          marginLeft: isSmartPhone ? "10px" : undefined,
        }}
      >
        {tableTitle}
        {totalCount != null ? (
          <Box component="span" sx={{ marginLeft: "6px", fontSize: "14px" }}>
            （{totalCount.toLocaleString()}件）
          </Box>
        ) : null}
        <IconButton
          sx={{ height: "25px", width: "25px", marginLeft: "5px" }}
          aria-label="detail row"
          size="small"
          onClick={() => {
            getTableData(false);
          }}
        >
          <ReplayIcon fontSize="small" />
        </IconButton>
        <FormControl
          fullWidth
          sx={{
            marginTop: "8px",
            position: "absolute",
            right: isSmartPhone
              ? "110px"
              : serchOpen || aiChatOpen
                ? "430px"
                : "130px",
            width: "80px",
          }}
        >
          <InputLabel
            id="display-number-label"
            sx={{
              fontSize: "14px",
            }}
          >
            表示数
          </InputLabel>
          <Select
            labelId="display-number-label"
            id="display-number"
            value={displayNumber.toString()}
            label="表示数"
            onChange={(e) => setDisplayNumber(Number(e.target.value))}
            sx={{
              height: "25px",
              textAlign: "end",
              fontSize: "14px",
            }}
          >
            {displayNumbers.map((displayNumber, i) => {
              return (
                <MenuItem key={"displayNumber" + i} value={displayNumber}>
                  {displayNumber}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <IconButton
          disabled={pageNumber === 1}
          aria-label="detail row"
          size="small"
          sx={{
            marginTop: "8px",
            position: "absolute",
            right: isSmartPhone
              ? "85px"
              : serchOpen || aiChatOpen
                ? "405px"
                : "105px",
          }}
          onClick={() => setPageNumber(pageNumber - 1)}
        >
          <ArrowBackIosNewIcon
            fontSize="small"
            sx={{ height: "15px", width: "15px" }}
          />
        </IconButton>
        <FormControl
          fullWidth
          sx={{
            marginTop: "8px",
            position: "absolute",
            right: isSmartPhone
              ? "20px"
              : serchOpen || aiChatOpen
                ? "335px"
                : "35px",
            width: "70px",
          }}
        >
          <InputLabel
            id="page-number-label"
            sx={{
              fontSize: "14px",
            }}
          >
            ページ
          </InputLabel>
          <Select
            labelId="page-number-label"
            id="page-number"
            value={pageNumber.toString()}
            label="ページ"
            onChange={(e) => setPageNumber(Number(e.target.value))}
            sx={{
              height: "25px",
              textAlign: "end",
              fontSize: "14px",
            }}
          >
            {pageList}
          </Select>
        </FormControl>
        <IconButton
          disabled={pageNumber === maxPageNumber}
          aria-label="detail row"
          size="small"
          sx={{
            marginTop: "8px",
            position: "absolute",
            right: isSmartPhone
              ? "0px"
              : serchOpen || aiChatOpen
                ? "310px"
                : "10px",
          }}
          onClick={() => setPageNumber(pageNumber + 1)}
        >
          <ArrowForwardIosIcon
            fontSize="small"
            sx={{ height: "15px", width: "15px" }}
          />
        </IconButton>
      </Box>
    </Typography>
  );
}

interface SubTableRowProps {
  subTableColumn: SubTableColumn;
  getSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  getSubSubTableName: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  };
  setGetSubTableEditDirect: (value: EditDataDifine | null) => void;
  setGetSubSubTableName: (value: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  }) => void;
  getSubSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  setGetSubSubTableAdd: (value: {
    name: string;
    row: dataDifine | null;
  }) => void;
  setGetSubSubTableEditDirect: (value: EditDataDifine | null) => void;
  setTableOriginalFunc: (value: {
    name: string;
    key: { [key: string]: string };
    subTableColumnName?: string;
  }) => void;
  index: number;
  sub_data: subDataDifine;
}
function SubTableRow(props: SubTableRowProps) {
  const {
    subTableColumn,
    getSubTableDetail,
    setGetSubTableDetail,
    getSubSubTableName,
    setGetSubTableEditDirect,
    setGetSubSubTableName,
    getSubSubTableDetail,
    setGetSubSubTableDetail,
    setGetSubSubTableAdd,
    setGetSubSubTableEditDirect,
    setTableOriginalFunc,
    index,
    sub_data,
  } = props;
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const setIsLoading = useSetAtom(AppAtoms.isLoading);
  const pathname = useAtomValue(AppAtoms.pathname);
  const [openTooltip, setOpenTooltip] = useState(false);
  async function downloadContractPicture(destination_key: string) {
    setIsLoading(true);
    const header_location = await getPresignedGetPicture(destination_key);
    if (header_location != "None") {
      const url = header_location;
      const link = document.createElement("a");
      link.href = header_location;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      link.parentNode?.removeChild(link);
    }
    setIsLoading(false);
  }
  async function downloadPaymentExcel(
    payment_year: string,
    payment_month: string,
    payment_sale_company_id: number,
  ) {
    setIsLoading(true);
    const responce = await getPaymentConfirmExcel({
      payment_year,
      payment_month,
      payment_sale_company_id,
    });
    if (responce != "None") {
      const url = responce;
      const link = document.createElement("a");
      link.href = responce;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      link.parentNode?.removeChild(link);
    }
    setIsLoading(false);
  }
  return (
    <Fragment>
      <TableRow
        key={"subTableData" + index}
        sx={{
          "& > *": {
            borderBottom: "unset",
          },
        }}
        style={{
          backgroundColor:
            index % 2 === 1 ? CONST.tableBackgroundColor : undefined,
        }}
      >
        {subTableColumn.subDataColumn?.map(
          (subDataColumn: SubDataColumn, i) => {
            if (subDataColumn.table) {
              return (
                <LightTooltip
                  onClose={() => setOpenTooltip(false)}
                  open={openTooltip && subDataColumn.key === "entry_url"}
                  title="コピーしました"
                >
                  <TableCell
                    key={"tableSubData" + i}
                    align="center"
                    component="th"
                    scope="row"
                  >
                    {subDataColumn.tableValue == "downloadPaymentExcel" ? (
                      <Box
                        className="table-body"
                        onClick={() => {
                          downloadPaymentExcel(
                            sub_data["payment_year"] as string,
                            sub_data["payment_month"] as string,
                            sub_data["payment_sale_company_id"] as number,
                          );
                        }}
                      >
                        <IconButton
                          sx={{ height: "25px", width: "25px" }}
                          aria-label="detail row"
                          size="small"
                        >
                          <FileDownloadIcon fontSize="medium" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box
                        className="table-body"
                        onClick={() => {
                          if (
                            sub_data.option_type === 4 &&
                            sub_data[subDataColumn.key] &&
                            sub_data[subDataColumn.key] != "" &&
                            subDataColumn.key == "customer_info_value"
                          ) {
                            downloadContractPicture(
                              sub_data[subDataColumn.key] as string,
                            );
                          }
                        }}
                      >
                        {sub_data.option_type === 4 &&
                        sub_data[subDataColumn.key] &&
                        sub_data[subDataColumn.key] != "" &&
                        subDataColumn.key == "customer_info_value"
                          ? (sub_data[subDataColumn.key] as string)
                              .split("/")
                              .pop()
                          : subTableColumn.subTableColumnName ===
                                "閲覧可能グループ一覧" &&
                              subDataColumn.key === "group_name" &&
                              sub_data["sale_company_name"] != null &&
                              sub_data["sale_company_name"] !== ""
                            ? `${sub_data["sale_company_name"]} / ${sub_data["group_name"]}`
                            : sub_data[subDataColumn.key]}
                        {sub_data.option_type === 4 &&
                        sub_data[subDataColumn.key] &&
                        sub_data[subDataColumn.key] != "" &&
                        subDataColumn.key == "customer_info_value" ? (
                          <IconButton
                            sx={{
                              height: "25px",
                              width: "25px",
                            }}
                            aria-label="detail row"
                            size="small"
                          >
                            <FileDownloadIcon fontSize="medium" />
                          </IconButton>
                        ) : null}
                        {subDataColumn.key === "entry_url" ? (
                          <IconButton
                            aria-label="detail row"
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                sub_data[subDataColumn.key] as string,
                              );
                              setOpenTooltip(true);
                            }}
                          >
                            <ContentCopyIcon fontSize="medium" />
                          </IconButton>
                        ) : null}
                      </Box>
                    )}
                  </TableCell>
                </LightTooltip>
              );
            }
          },
        )}

        {subTableColumn.subTable.map((subTable, subTable_i) => {
          let dataDetailKey: { [key: string]: string } = {};
          let openSubTable = true;
          subTableColumn.detailKey.map((key) => {
            dataDetailKey[key] = sub_data[key] as string;
            if (getSubSubTableName.key[key] !== sub_data[key]) {
              openSubTable = false;
            }
          });
          return (
            <>
              <TableCell
                key={"tableColumnSunSub_" + subTable_i}
                align="center"
                component="th"
                scope="row"
                sx={{
                  minWidth: subTable.subTableColumnNameWidth,
                  maxWidth: isSmartPhone
                    ? subTable.subTableColumnNameWidth
                    : undefined,
                }}
              >
                {pathname === "/toss_up" &&
                sub_data[subTable.subTableColumnLength] === 1 ? (
                  <Box className="table-body">
                    {
                      sub_data[
                        subTable.subTableColumnLength + "_name"
                      ] as string
                    }
                  </Box>
                ) : (
                  <IconButton
                    disabled={
                      sub_data[subTable.subTableColumnLength] == undefined
                        ? true
                        : (sub_data[
                            subTable.subTableColumnLength
                          ] as number) === 0
                    }
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="expand row"
                    size="small"
                    onClick={() => {
                      if (
                        getSubSubTableName.name ===
                          subTable.subTableColumnName &&
                        openSubTable
                      ) {
                        setGetSubSubTableName({
                          name: "",
                          key: {},
                          subData: [],
                        });
                      } else {
                        setGetSubSubTableName({
                          name: subTable.subTableColumnName,
                          key: dataDetailKey,
                          subData: [],
                        });
                      }
                    }}
                  >
                    {getSubSubTableName.name === subTable.subTableColumnName &&
                    openSubTable &&
                    getSubSubTableName.subData.length > 0 ? (
                      <KeyboardArrowUpIcon fontSize="medium" />
                    ) : (
                      <KeyboardArrowDownIcon fontSize="medium" />
                    )}
                  </IconButton>
                )}
              </TableCell>
              {subTable.subTableAdd ? (
                <TableCell
                  key={"tableColumnSubSubAdd_" + subTable_i}
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: subTable.subTableAddTitleWidth,
                    maxWidth: isSmartPhone
                      ? subTable.subTableAddTitleWidth
                      : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      subTable.subTableAddTitle === "リストID追加" &&
                      (sub_data["customer_list_id_flg"] === "使用しない" ||
                        sub_data["customer_list_id_flg"] === "-")
                        ? true
                        : false
                    }
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="expand row"
                    size="small"
                    onClick={() => {
                      setGetSubSubTableAdd({
                        name: subTable.subTableAddTitle,
                        row: sub_data,
                      });
                    }}
                  >
                    <AddIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              ) : null}
            </>
          );
        })}
        {loginUser &&
        subTableColumn.subTableEditAuth.includes(loginUser.authority) &&
        subTableColumn.subTableEditDirect ? (
          <TableCell
            key="subTableDataDetail"
            align="center"
            component="th"
            scope="row"
          >
            <IconButton
              sx={{ height: "25px", width: "25px" }}
              aria-label="detail row"
              size="small"
              onClick={async () => {
                if (subTableColumn.subTableColumnName === "顧客情報一覧") {
                  if (
                    sub_data.option_type === 0 ||
                    sub_data.option_type === 3 ||
                    sub_data.option_type === 4
                  ) {
                    setGetSubTableEditDirect({
                      status: "before",
                      editType:
                        sub_data.option_type === 0
                          ? sub_data.multi_text_flg === 1
                            ? "customer_multitext"
                            : "customer_text"
                          : sub_data.option_type === 3
                            ? "customer_date"
                            : "customer_picture",
                      tableId: sub_data.id as string,
                      id: sub_data.id as number,
                      key: sub_data.customer_info_key as string,
                      name: sub_data.customer_info_key as string,
                      value: sub_data.customer_info as string,
                      editValue: sub_data.customer_info as string,
                      editPhoneNumber:
                        sub_data.tel_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      editName:
                        sub_data.name_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      editCustomerName:
                        sub_data.company_name_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      tel_flg: sub_data.tel_flg as number,
                      name_flg: sub_data.name_flg as number,
                      company_name_flg: sub_data.company_name_flg as number,
                      half_width_number_flg:
                        sub_data.half_width_number_flg as number,
                      customerList: [],
                      payment_method_id: "",
                      service_id: sub_data.service_id as string,
                      customer_picture: null,
                    });
                  } else {
                    setIsLoading(true);
                    setGetSubTableEditDirect({
                      status: "editing",
                      editType:
                        sub_data.option_type === 0
                          ? sub_data.multi_text_flg === 1
                            ? "customer_multitext"
                            : "customer_text"
                          : sub_data.option_type === 1
                            ? "customer_select_one"
                            : sub_data.option_type === 2
                              ? "customer_select_multiple"
                              : sub_data.option_type === 3
                                ? "customer_date"
                                : "customer_picture",
                      tableId: sub_data.id as string,
                      id: sub_data.id as number,
                      key: sub_data.customer_info_key as string,
                      name: sub_data.customer_info_key as string,
                      value: sub_data.customer_info as string,
                      editValue: sub_data.customer_info as string,
                      editPhoneNumber:
                        sub_data.tel_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      editName:
                        sub_data.name_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      editCustomerName:
                        sub_data.company_name_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      tel_flg: sub_data.tel_flg as number,
                      name_flg: sub_data.name_flg as number,
                      company_name_flg: sub_data.company_name_flg as number,
                      half_width_number_flg:
                        sub_data.half_width_number_flg as number,
                      customerList: [],
                      payment_method_id: "",
                      service_id: sub_data.service_id as string,
                      customer_picture: null,
                    });
                    // 質問の選択肢一覧取得
                    const optionSelects =
                      pathname == "/toss_up"
                        ? await getOptionSelectsTossUp(
                            sub_data.option_id as number,
                          )
                        : sub_data.entry_method === 0
                          ? await getOptionSelects(sub_data.option_id as number)
                          : sub_data.entry_method === 1
                            ? await getOptionSelectsOutside(
                                sub_data.option_id as number,
                              )
                            : [];

                    setGetSubTableEditDirect({
                      status: "before",
                      editType:
                        sub_data.option_type === 0
                          ? sub_data.multi_text_flg === 1
                            ? "customer_multitext"
                            : "customer_text"
                          : sub_data.option_type === 1
                            ? "customer_select_one"
                            : sub_data.option_type === 2
                              ? "customer_select_multiple"
                              : sub_data.option_type === 3
                                ? "customer_date"
                                : "customer_picture",
                      tableId: sub_data.id as string,
                      id: sub_data.id as number,
                      key: sub_data.customer_info_key as string,
                      name: sub_data.customer_info_key as string,
                      value: sub_data.customer_info as string,
                      editValue: sub_data.customer_info as string,
                      editPhoneNumber:
                        sub_data.tel_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      editName:
                        sub_data.name_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      editCustomerName:
                        sub_data.company_name_flg === 1
                          ? (sub_data[
                              subTableColumn.subTableEditDirectKey
                            ] as string)
                          : "",
                      tel_flg: sub_data.tel_flg as number,
                      name_flg: sub_data.name_flg as number,
                      company_name_flg: sub_data.company_name_flg as number,
                      customerList: optionSelects,
                      payment_method_id: "",
                      service_id: sub_data.service_id as string,
                      customer_picture: null,
                    });
                    setIsLoading(false);
                  }
                } else {
                  setGetSubTableEditDirect({
                    status: "before",
                    editType:
                      sub_data.editType === "list"
                        ? "list"
                        : sub_data.editType === "multitext"
                          ? "multitext"
                          : "text",
                    tableId: sub_data.id as string,
                    id: sub_data.id as number,
                    key: subTableColumn.subTableEditDirectKey as string,
                    name: subTableColumn.subTableColumnName as string,
                    value: sub_data[
                      subTableColumn.subTableEditDirectKey
                    ] as string,
                    editValue: sub_data[
                      subTableColumn.subTableEditDirectKey
                    ] as string,
                    editPhoneNumber: "",
                    editName: "",
                    editCustomerName: "",
                    tel_flg: 0,
                    name_flg: 0,
                    company_name_flg: 0,
                    half_width_number_flg: 0,
                    customerList: [],
                    payment_method_id: sub_data.payment_method_id as string,
                    service_id: sub_data.service_id as string,
                    customer_picture: null,
                  });
                }
              }}
            >
              <EditNoteIcon fontSize="medium" />
            </IconButton>
          </TableCell>
        ) : null}
        {subTableColumn.subTableDetail ? (
          <TableCell
            key="subTableDataDetail"
            align="center"
            component="th"
            scope="row"
          >
            <IconButton
              sx={{ height: "25px", width: "25px" }}
              aria-label="detail row"
              size="small"
              onClick={() => {
                let dataDetailKey: { [key: string]: string } = {};
                subTableColumn.detailKey.map((key) => {
                  dataDetailKey[key] = sub_data[key] as string;
                });
                setGetSubTableDetail({
                  ...getSubTableDetail,
                  key: dataDetailKey,
                  name: subTableColumn.subTableDetailTitle,
                });
              }}
            >
              <MoreHorizIcon fontSize="medium" />
            </IconButton>
          </TableCell>
        ) : null}
        {subTableColumn.tableFunction.map((tableFunction) => {
          if (loginUser && tableFunction.auth.includes(loginUser.authority)) {
            if (tableFunction.name === "削除") {
              return (
                <TableCell
                  key="tableDataCancell"
                  align="center"
                  component="th"
                  scope="row"
                >
                  <IconButton
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: {
                        [key: string]: string;
                      } = {};
                      subTableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = sub_data[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                        subTableColumnName: subTableColumn.subTableColumnName,
                      });
                    }}
                  >
                    <DeleteIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            }
          }
        })}
      </TableRow>
      {subTableColumn.subTable.map((subTable, subTable_i) => {
        let dataDetailKey: { [key: string]: string } = {};
        let openSubTable = true;
        subTableColumn.detailKey.map((key) => {
          dataDetailKey[key] = sub_data[key] as string;
          if (getSubSubTableName.key[key] !== sub_data[key]) {
            openSubTable = false;
          }
        });
        if (
          getSubSubTableName.name === subTable.subTableColumnName &&
          openSubTable &&
          getSubSubTableName.subData.length > 0
        ) {
          return (
            <SubTable
              open={
                getSubSubTableName.name === subTable.subTableColumnName &&
                openSubTable &&
                getSubSubTableName.subData.length > 0
              }
              subTableColumn={subTable}
              getSubTableName={getSubSubTableName}
              getSubTableDetail={getSubSubTableDetail}
              setGetSubTableDetail={setGetSubSubTableDetail}
              setGetSubTableEditDirect={setGetSubSubTableEditDirect}
              getSubSubTableName={{
                name: "",
                key: {},
                subData: [],
              }}
              setGetSubSubTableName={() => {}}
              getSubSubTableDetail={{
                name: "",
                key: {},
                dataDetail: null,
              }}
              setGetSubSubTableDetail={() => {}}
              setGetSubSubTableAdd={() => {}}
              setGetSubSubTableEditDirect={() => {}}
              setTableOriginalFunc={setTableOriginalFunc}
            />
          );
        }
      })}
    </Fragment>
  );
}

interface SubTableProps {
  open: boolean;
  getSubTableName: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[] | null;
  };
  subTableColumn: SubTableColumn;
  getSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  getSubSubTableName: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  };
  setGetSubTableEditDirect: (value: EditDataDifine | null) => void;
  setGetSubSubTableName: (value: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  }) => void;
  getSubSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  setGetSubSubTableAdd: (value: {
    name: string;
    row: dataDifine | null;
  }) => void;
  setGetSubSubTableEditDirect: (value: EditDataDifine | null) => void;
  setTableOriginalFunc: (value: {
    name: string;
    key: { [key: string]: string };
    subTableColumnName?: string;
  }) => void;
}
function SubTable(props: SubTableProps) {
  const {
    open,
    getSubTableName,
    subTableColumn,
    getSubTableDetail,
    setGetSubTableDetail,
    getSubSubTableName,
    setGetSubTableEditDirect,
    setGetSubSubTableName,
    getSubSubTableDetail,
    setGetSubSubTableDetail,
    setGetSubSubTableAdd,
    setGetSubSubTableEditDirect,
    setTableOriginalFunc,
  } = props;
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const subTableWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !isSmartPhone) return;
    const handleScroll = () => {
      if (subTableWrapperRef.current) {
        subTableWrapperRef.current.style.transform = `translateX(${window.scrollX}px)`;
      }
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (subTableWrapperRef.current) {
        subTableWrapperRef.current.style.transform = "";
      }
    };
  }, [open, isSmartPhone]);

  return (
    <TableRow>
      <TableCell
        colSpan={99}
        sx={{
          p: 0,
          backgroundColor: "white",
        }}
      >
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box
            ref={subTableWrapperRef}
            sx={{
              position: isSmartPhone ? undefined : "sticky",
              left: isSmartPhone ? undefined : 0,
              maxWidth: isSmartPhone
                ? "calc(100vw - 20px)"
                : "calc(100vw - 50px)",
              overflowX: "auto",
              overscrollBehaviorX: "contain",
            }}
          >
            <Box
              sx={{
                border: "1px solid rgb(230, 230, 230)",
                minWidth: "max-content",
                margin: "8px 12px",
              }}
            >
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    {subTableColumn.subDataColumn?.map((subDataColumn, i) => {
                      if (subDataColumn.table) {
                        return (
                          <TableCell
                            sx={{
                              backgroundColor: CONST.tableBackgroundColor,
                            }}
                            key={"subTableColumn" + i}
                            align="center"
                          >
                            <Box className="table-header">
                              {subDataColumn.name}
                            </Box>
                          </TableCell>
                        );
                      }
                    })}
                    {subTableColumn.subTable.map((subTable, subTable_i) => {
                      return (
                        <>
                          <TableCell
                            sx={{
                              backgroundColor: CONST.tableBackgroundColor,
                              minWidth: subTable.subTableColumnNameWidth,
                              maxWidth: isSmartPhone
                                ? subTable.subTableColumnNameWidth
                                : undefined,
                            }}
                            key={"tableColumnSubSub" + subTable_i}
                            align="center"
                          >
                            <Box className="table-header">
                              {subTable.subTableColumnName}
                            </Box>
                          </TableCell>
                          {subTable.subTableAdd ? (
                            <TableCell
                              sx={{
                                backgroundColor: CONST.tableBackgroundColor,
                                minWidth: subTable.subTableAddTitleWidth,
                                maxWidth: isSmartPhone
                                  ? subTable.subTableAddTitleWidth
                                  : undefined,
                              }}
                              key={"tableColumnSubSubAdd" + subTable_i}
                              align="center"
                            >
                              <Box className="table-header">
                                {subTable.subTableAddTitle}
                              </Box>
                            </TableCell>
                          ) : null}
                        </>
                      );
                    })}
                    {loginUser &&
                    subTableColumn.subTableEditAuth.includes(
                      loginUser.authority,
                    ) &&
                    subTableColumn.subTableEditDirect ? (
                      <TableCell
                        sx={{
                          backgroundColor: CONST.tableBackgroundColor,
                        }}
                        key="subTableEditDirectColumn"
                        align="center"
                      >
                        <Box className="table-header">
                          {subTableColumn.subTableEditTitle}
                        </Box>
                      </TableCell>
                    ) : null}
                    {subTableColumn.subTableDetail ? (
                      <TableCell
                        sx={{
                          backgroundColor: CONST.tableBackgroundColor,
                        }}
                        key="subTableColumnDetail"
                        align="center"
                      >
                        <Box className="table-header">詳細</Box>
                      </TableCell>
                    ) : null}
                    {subTableColumn.tableFunction.map((tableFunction, i) => {
                      if (
                        loginUser &&
                        tableFunction.auth.includes(loginUser.authority)
                      ) {
                        return (
                          <TableCell
                            sx={{
                              backgroundColor: CONST.tableBackgroundColor,
                            }}
                            key={"tableColumnFunc" + i}
                            align="center"
                          >
                            <Box className="table-header">
                              {tableFunction.name}
                            </Box>
                          </TableCell>
                        );
                      }
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSubTableName.subData?.map((sub_data, index) => {
                    return (
                      <SubTableRow
                        subTableColumn={subTableColumn}
                        getSubTableDetail={getSubTableDetail}
                        setGetSubTableDetail={setGetSubTableDetail}
                        getSubSubTableName={getSubSubTableName}
                        setGetSubTableEditDirect={setGetSubTableEditDirect}
                        setGetSubSubTableName={setGetSubSubTableName}
                        getSubSubTableDetail={getSubSubTableDetail}
                        setGetSubSubTableDetail={setGetSubSubTableDetail}
                        setGetSubSubTableAdd={setGetSubSubTableAdd}
                        setGetSubSubTableEditDirect={
                          setGetSubSubTableEditDirect
                        }
                        setTableOriginalFunc={setTableOriginalFunc}
                        index={index}
                        sub_data={sub_data}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
}

export interface RowProps {
  row: dataDifine;
  index: number;
  tableColumn: TableColumn;
  screenDataColumn: DataColumn[];
  getDataDetail: (key: { [key: string]: string }, edit: boolean) => void;
  getSubTableName: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  };
  setGetSubTableName: (value: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  }) => void;
  getSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  setGetSubTableAdd: (value: { name: string; row: dataDifine | null }) => void;
  getSubSubTableName: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  };
  setGetSubTableEditDirect: (value: EditDataDifine | null) => void;
  setGetSubSubTableName: (value: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  }) => void;
  getSubSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  setGetSubSubTableAdd: (value: {
    name: string;
    row: dataDifine | null;
  }) => void;
  setGetSubSubTableEditDirect: (value: EditDataDifine | null) => void;
  switchTableCheckBox: (dataKey: { [key: string]: string }) => void;
  editIDs: number[];
  setEditIDs: (value: number[]) => void;
  setGetSubMainTableName: (value: {
    name: string;
    key: { [key: string]: string };
  }) => void;
  setTableOriginalFunc: (value: {
    name: string;
    key: { [key: string]: string };
    subTableColumnName?: string;
  }) => void;
}

function Row(props: RowProps) {
  const {
    row,
    index,
    tableColumn,
    screenDataColumn,
    getDataDetail,
    getSubTableName,
    setGetSubTableName,
    getSubTableDetail,
    setGetSubTableDetail,
    setGetSubTableAdd,
    setGetSubTableEditDirect,
    getSubSubTableName,
    setGetSubSubTableName,
    getSubSubTableDetail,
    setGetSubSubTableDetail,
    setGetSubSubTableAdd,
    setGetSubSubTableEditDirect,
    switchTableCheckBox,
    editIDs,
    setEditIDs,
    setGetSubMainTableName,
    setTableOriginalFunc,
  } = props;

  const loginUser = useAtomValue(AppAtoms.loginUser);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const pathname = useAtomValue(AppAtoms.pathname);
  const [openTooltip, setOpenTooltip] = useState("");
  const setIsLoading = useSetAtom(AppAtoms.isLoading);
  async function downloadPaymentExcel(
    payment_year: string,
    payment_month: string,
    payment_sale_company_id: number,
  ) {
    setIsLoading(true);
    const responce = await getPaymentConfirmExcel({
      payment_year,
      payment_month,
      payment_sale_company_id,
    });
    if (responce != "None") {
      const url = responce;
      const link = document.createElement("a");
      link.href = responce;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      link.parentNode?.removeChild(link);
    }
    setIsLoading(false);
  }
  return (
    <Fragment>
      <TableRow
        sx={{
          "& > *": {
            borderBottom: "unset",
          },
        }}
        style={{
          backgroundColor:
            pathname === "/toss_up"
              ? row.toss_up_status === "トスアップ"
                ? "white"
                : row.toss_up_status === "クロージング-成功（未エントリー）"
                  ? row.category_length === 1 &&
                    row.category_length_name === "インターネット"
                    ? "#ff4daf"
                    : "#fa82c4"
                  : row.toss_up_status === "不出"
                    ? row.category_length === 1 &&
                      row.category_length_name === "インターネット"
                      ? "#f5a100"
                      : "#ffb039"
                    : row.toss_up_status === "時節"
                      ? row.category_length === 1 &&
                        row.category_length_name === "インターネット"
                        ? "#87c9a5"
                        : "#8cfa78"
                      : row.toss_up_status === "見込み"
                        ? row.category_length === 1 &&
                          row.category_length_name === "インターネット"
                          ? "#fff100"
                          : "#fffacd"
                        : row.toss_up_status === "架電中"
                          ? "#33ffff"
                          : index % 2 === 1
                            ? CONST.tableBackgroundColor
                            : undefined
              : pathname === "/follow_call"
                ? row.follow_call_status === "フォローコール未実施"
                  ? "white"
                  : row.follow_call_status === "不出"
                    ? "#ffb039"
                    : row.follow_call_status === "時節"
                      ? "#8cfa78"
                      : row.follow_call_status === "見込み"
                        ? "#fffacd"
                        : index % 2 === 1
                          ? CONST.tableBackgroundColor
                          : undefined
                : index % 2 === 1
                  ? CONST.tableBackgroundColor
                  : undefined,
        }}
      >
        {screenDataColumn.map((dataColumn, i) => {
          if (dataColumn.table) {
            if (dataColumn.key === "contract_number_months") {
              return getOneYearList().map((yearMonth, year_i) => {
                return (
                  <TableCell
                    key={"tableYearMonthData" + year_i}
                    align="center"
                    component="th"
                    scope="row"
                    sx={{
                      minWidth: isSmartPhone
                        ? dataColumn.tableMinWidth
                        : undefined,
                      maxWidth: isSmartPhone
                        ? dataColumn.tableMinWidth
                        : undefined,
                    }}
                  >
                    <Box className="table-body">
                      {row[dataColumn.key + year_i] as string}
                    </Box>
                  </TableCell>
                );
              });
            } else {
              if (
                loginUser &&
                dataColumn.tableDetailAuth.includes(loginUser.authority)
              ) {
                return (
                  <LightTooltip
                    onClose={() => setOpenTooltip("")}
                    open={openTooltip == dataColumn.key}
                    title="コピーしました"
                  >
                    <TableCell
                      key={"tableData" + i}
                      align="center"
                      component="th"
                      scope="row"
                      sx={{
                        minWidth: isSmartPhone
                          ? dataColumn.tableMinWidth
                          : undefined,
                        maxWidth: isSmartPhone
                          ? dataColumn.tableMinWidth
                          : undefined,
                      }}
                      onClick={() => {
                        if (dataColumn.key === "password") {
                          navigator.clipboard.writeText(
                            row["copy_password"] as string,
                          );
                          setOpenTooltip(dataColumn.key);
                        } else if (
                          dataColumn.key === "outside_entry_url" ||
                          dataColumn.key === "outside_cancell_url"
                        ) {
                          navigator.clipboard.writeText(
                            row[dataColumn.key] as string,
                          );
                          setOpenTooltip(dataColumn.key);
                        }
                      }}
                    >
                      {dataColumn.tableValue == "text" ? (
                        <Box className="table-body">
                          {row[dataColumn.key] as string}
                          {dataColumn.key === "password" ||
                          ((dataColumn.key === "outside_entry_url" ||
                            dataColumn.key === "outside_cancell_url") &&
                            row[dataColumn.key] !== "-") ? (
                            <IconButton aria-label="detail row" size="small">
                              <ContentCopyIcon fontSize="medium" />
                            </IconButton>
                          ) : null}
                        </Box>
                      ) : dataColumn.tableValue == "check" ? (
                        <IconButton
                          sx={{ height: "25px", width: "25px" }}
                          aria-label="detail row"
                          size="small"
                        >
                          <Checkbox
                            disabled={row[dataColumn.key + "_disable"] != "1"}
                            checked={row[dataColumn.key] == "1"}
                            onClick={() => {
                              let dataDetailKey: { [key: string]: string } = {};
                              tableColumn.detailKey.map((key) => {
                                dataDetailKey[key] = row[key] as string;
                              });
                              dataDetailKey["key"] = dataColumn.key;
                              switchTableCheckBox(dataDetailKey);
                            }}
                            sx={{
                              "& .MuiSvgIcon-root": { fontSize: 18 },
                            }}
                          />
                        </IconButton>
                      ) : dataColumn.tableValue == "edit_check" ? (
                        <IconButton
                          sx={{ height: "25px", width: "25px" }}
                          aria-label="detail row"
                          size="small"
                        >
                          <Checkbox
                            disabled={row.id === "-"}
                            checked={editIDs.includes(row.id as number)}
                            onClick={() => {
                              let _editIDs = editIDs.concat();
                              if (editIDs.includes(row.id as number)) {
                                _editIDs = _editIDs.filter(
                                  (editID) => editID != row.id,
                                );
                              } else {
                                _editIDs.push(row.id as number);
                              }
                              setEditIDs(_editIDs);
                            }}
                            sx={{
                              "& .MuiSvgIcon-root": { fontSize: 18 },
                            }}
                          />
                        </IconButton>
                      ) : dataColumn.tableValue == "downloadPaymentExcel" ? (
                        <IconButton
                          disabled={
                            (row["payment_year"] as string) == "-" ||
                            (row["payment_month"] as string) == "-"
                          }
                          sx={{ height: "25px", width: "25px" }}
                          aria-label="detail row"
                          size="small"
                          onClick={() => {
                            downloadPaymentExcel(
                              row["payment_year"] as string,
                              row["payment_month"] as string,
                              row["payment_sale_company_id"] as number,
                            );
                          }}
                        >
                          <FileDownloadIcon fontSize="medium" />
                        </IconButton>
                      ) : null}
                    </TableCell>
                  </LightTooltip>
                );
              }
            }
          }
        })}

        {tableColumn.subTable.map((subTable, subTable_i) => {
          let dataDetailKey: { [key: string]: string } = {};
          let openSubTable = true;
          tableColumn.detailKey.map((key) => {
            dataDetailKey[key] = row[key] as string;
            if (getSubTableName.key[key] !== row[key]) {
              openSubTable = false;
            }
          });
          if (
            loginUser &&
            subTable.subTableAuth.includes(loginUser.authority)
          ) {
            return (
              <>
                <TableCell
                  key={"tableColumnSub_" + subTable_i + "_" + index}
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: subTable.subTableColumnNameWidth,
                    maxWidth: isSmartPhone
                      ? subTable.subTableColumnNameWidth
                      : undefined,
                  }}
                >
                  {pathname === "/toss_up" &&
                  row[subTable.subTableColumnLength] === 1 ? (
                    <Box className="table-body">
                      {row[subTable.subTableColumnLength + "_name"] as string}
                    </Box>
                  ) : (
                    <IconButton
                      disabled={
                        row[subTable.subTableColumnLength] == undefined ||
                        (row[subTable.subTableColumnLength] as number) === 0 ||
                        (subTable.subTableColumnName ===
                          "ログインパートナー一覧" &&
                          row.login_sale_company !==
                            "指定したログインパートナーのみ") ||
                        (subTable.subTableColumnName === "選択肢一覧" &&
                          (row.screen_column_system_default !== "独自項目" ||
                            (row.screen_column_type !== "プルダウン" &&
                              row.screen_column_type !== "チェックボックス")))
                      }
                      sx={{ height: "25px", width: "25px" }}
                      aria-label="expand row"
                      size="small"
                      onClick={() => {
                        if (
                          getSubTableName.name ===
                            subTable.subTableColumnName &&
                          openSubTable
                        ) {
                          setGetSubTableName({
                            name: "",
                            key: {},
                            subData: [],
                          });
                        } else {
                          setGetSubTableName({
                            name: subTable.subTableColumnName,
                            key: dataDetailKey,
                            subData: [],
                          });
                        }
                      }}
                    >
                      {getSubTableName.name === subTable.subTableColumnName &&
                      openSubTable &&
                      getSubTableName.subData.length > 0 ? (
                        <KeyboardArrowUpIcon fontSize="medium" />
                      ) : (
                        <KeyboardArrowDownIcon fontSize="medium" />
                      )}
                    </IconButton>
                  )}
                </TableCell>
                {subTable.subTableAdd ? (
                  <TableCell
                    key={"tableColumnSubAdd_" + subTable_i + "_" + index}
                    align="center"
                    component="th"
                    scope="row"
                    sx={{
                      minWidth: subTable.subTableAddTitleWidth,
                      maxWidth: isSmartPhone
                        ? subTable.subTableAddTitleWidth
                        : undefined,
                    }}
                  >
                    <IconButton
                      disabled={
                        row.id === "-" ||
                        (subTable.subTableAddTitle === "解約SMS追加" &&
                          row.cancell_action !== "手動解約可能") ||
                        (subTable.subTableAddTitle === "閲覧可能商材追加" &&
                          row.authority !== "営業MGR" &&
                          row.authority !== "パートナー") ||
                        (subTable.subTableAddTitle === "閲覧可能グループ追加" &&
                          row.authority !== "営業MGR" &&
                          row.authority !== "パートナー") ||
                        (subTable.subTableAddTitle ===
                          "ログインパートナー追加" &&
                          row.login_sale_company !==
                            "指定したログインパートナーのみ") ||
                        (subTable.subTableAddTitle === "選択肢追加" &&
                          (row.screen_column_system_default !== "独自項目" ||
                            (row.screen_column_type !== "プルダウン" &&
                              row.screen_column_type !==
                                "チェックボックス"))) ||
                        (pathname === "/outside_entry_form" &&
                          subTable.subTableAddTitle === "グループ追加" &&
                          row.entry_sale_company_group !== "指定する")
                      }
                      sx={{ height: "25px", width: "25px" }}
                      aria-label="expand row"
                      size="small"
                      onClick={() => {
                        setGetSubTableAdd({
                          name: subTable.subTableAddTitle,
                          row,
                        });
                      }}
                    >
                      <AddIcon fontSize="medium" />
                    </IconButton>
                  </TableCell>
                ) : null}
                {subTable.mainTableDetail ? (
                  <TableCell
                    key="tableDataMainSubDetail"
                    align="center"
                    component="th"
                    scope="row"
                    sx={{
                      minWidth: isSmartPhone ? "140px" : undefined,
                      maxWidth: isSmartPhone ? "140px" : undefined,
                    }}
                  >
                    <IconButton
                      disabled={row.id === "-" ? true : false}
                      sx={{ height: "25px", width: "25px" }}
                      aria-label="detail row"
                      size="small"
                      onClick={() => {
                        if (
                          getSubTableName.name ===
                            subTable.mainTableDetailColumn &&
                          openSubTable
                        ) {
                          setGetSubMainTableName({
                            name: "",
                            key: {},
                          });
                        } else {
                          setGetSubMainTableName({
                            name: subTable.mainTableDetailColumn,
                            key: dataDetailKey,
                          });
                        }
                      }}
                    >
                      <MoreHorizIcon fontSize="medium" />
                    </IconButton>
                  </TableCell>
                ) : null}
              </>
            );
          }
        })}
        {tableColumn.detail ? (
          <TableCell
            key="tableDataDetail"
            align="center"
            component="th"
            scope="row"
            sx={{
              minWidth: isSmartPhone ? "60px" : undefined,
              maxWidth: isSmartPhone ? "60px" : undefined,
            }}
          >
            <IconButton
              disabled={row.id === "-"}
              sx={{ height: "25px", width: "25px" }}
              aria-label="detail row"
              size="small"
              onClick={() => {
                let dataDetailKey: { [key: string]: string } = {};
                tableColumn.detailKey.map((key) => {
                  dataDetailKey[key] = row[key] as string;
                });
                getDataDetail(dataDetailKey, false);
              }}
            >
              <MoreHorizIcon fontSize="medium" />
            </IconButton>
          </TableCell>
        ) : null}
        {tableColumn.tableFunction.map((tableFunction) => {
          if (loginUser && tableFunction.auth.includes(loginUser.authority)) {
            if (tableFunction.name === "エントリー取消") {
              return (
                <TableCell
                  key="tableDataCancell"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.opening_date_func !== "-" ||
                      row.contract_deactivation === 1
                    }
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <DeleteIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "トスアップ複製") {
              return (
                <TableCell
                  key="tableDataCopy"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      pathname === "/toss_up"
                        ? row.toss_up_status_func !== "クロージング-成功" &&
                          row.toss_up_status_func !== "クロージング-失敗" &&
                          row.toss_up_status_func !== "対象外" &&
                          row.toss_up_status_func !== "条件期間外" &&
                          row.toss_up_status_func !== "長期不出"
                        : false
                    }
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <ContentCopyIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "トスアップ取消") {
              return (
                <TableCell
                  key="tableDataCancell"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.toss_up_status_func === "クロージング-成功" ||
                      row.toss_up_status_func === "クロージング-失敗" ||
                      row.toss_up_status_func == "対象外" ||
                      row.toss_up_status_func == "条件期間外" ||
                      row.toss_up_status_func == "長期不出" ||
                      row.toss_up_deactivation === 1
                    }
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <DeleteIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "解約") {
              return (
                <TableCell
                  key="tableDataDelete"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.cancell_action !== "手動解約可能" ||
                      row.apply_date_func === "-" ||
                      row.cancell_date_func !== "-" ||
                      row.contract_deactivation === 1
                    }
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <CancelIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "パスワードリセット") {
              return (
                <TableCell
                  key="tableDataDelete"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={row.login_status == "ログイン不可能"}
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <VpnKeyIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "クロージング") {
              return (
                <TableCell
                  key="tableDataClose"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.toss_up_status_func == "クロージング-成功" ||
                      row.toss_up_status_func == "クロージング-失敗" ||
                      row.toss_up_status_func == "対象外" ||
                      row.toss_up_status_func == "条件期間外" ||
                      row.toss_up_status_func == "長期不出" ||
                      row.closable_flg === 0 ||
                      row.closable_flg === "0"
                    }
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <AddCircleOutlineIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "取扱い停止・再開") {
              return (
                <TableCell
                  key="tableDataEnd"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={row.no_entry_flg_pd === 1}
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: {
                          ...dataDetailKey,
                          startStop:
                            row.no_entry_flg_sr === 1 ? "start" : "stop",
                        },
                      });
                    }}
                  >
                    {row.no_entry_flg_sr === 1 ? (
                      <PlayCircleOutlineIcon fontSize="medium" />
                    ) : (
                      <DoDisturbAltIcon fontSize="medium" />
                    )}
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "公開・非公開") {
              return (
                <TableCell
                  key="tableDataEnd"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.id === "-" || row.site_status === "商材停止中"
                    }
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: {
                          ...dataDetailKey,
                          startStop:
                            row.site_status === "商材停止中" ||
                            row.site_status === "閉鎖中"
                              ? "start"
                              : "stop",
                        },
                      });
                    }}
                  >
                    {row.site_status === "商材停止中" ||
                    row.site_status === "閉鎖中" ? (
                      <PlayCircleOutlineIcon fontSize="medium" />
                    ) : (
                      <DoDisturbAltIcon fontSize="medium" />
                    )}
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "削除") {
              return (
                <TableCell
                  key="tableDataEnd"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      pathname === "/follow_call_config" && row.id === "-"
                    }
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <DeleteIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "項目削除") {
              return (
                <TableCell
                  key="tableDataEnd"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.screen_column_system_default === "デフォルト項目"
                    }
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <DeleteIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "コピー") {
              return (
                <TableCell
                  key="tableDataEnd"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={row.id === "-"}
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <ContentCopyIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "個人情報削除") {
              return (
                <TableCell
                  key="tablePersonDataDelete"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.cancell_date_func === "-" ||
                      row.customer_phone_number_func === "削除済"
                    }
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <PersonOffIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "停止・再開") {
              return (
                <TableCell
                  key="tableDataEnd"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={row.follow_call_status === "フォローコール完了"}
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: {
                          ...dataDetailKey,
                          startStop:
                            row.follow_call_status ===
                            "フォローコールを行わない"
                              ? "start"
                              : "stop",
                        },
                      });
                    }}
                  >
                    {row.follow_call_status === "フォローコールを行わない" ? (
                      <AddIcCallIcon fontSize="medium" />
                    ) : (
                      <DoDisturbAltIcon fontSize="medium" />
                    )}
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "フォローコール") {
              return (
                <TableCell
                  key="tableDataEnd"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={
                      row.follow_call_status === "フォローコールを行わない" ||
                      row.follow_call_status === "フォローコール完了"
                    }
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <CallIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "体制図") {
              return (
                <TableCell
                  key="tableDataChart"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="chart row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <AccountTreeIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            } else if (tableFunction.name === "エントリー複製") {
              return (
                <TableCell
                  key="tableDataEntrycopy"
                  align="center"
                  component="th"
                  scope="row"
                  sx={{
                    minWidth: isSmartPhone ? tableFunction.width : undefined,
                    maxWidth: isSmartPhone ? tableFunction.width : undefined,
                  }}
                >
                  <IconButton
                    disabled={row.contract_deactivation === 1}
                    sx={{ height: "25px", width: "25px" }}
                    aria-label="detail row"
                    size="small"
                    onClick={() => {
                      let dataDetailKey: { [key: string]: string } = {};
                      tableColumn.detailKey.map((key) => {
                        dataDetailKey[key] = row[key] as string;
                      });
                      setTableOriginalFunc({
                        name: tableFunction.name,
                        key: dataDetailKey,
                      });
                    }}
                  >
                    <ContentCopyIcon fontSize="medium" />
                  </IconButton>
                </TableCell>
              );
            }
          }
        })}
      </TableRow>
      {tableColumn.subTable.map((subTable, subTable_i) => {
        let dataDetailKey: { [key: string]: string } = {};
        let openSubTable = true;
        tableColumn.detailKey.map((key) => {
          dataDetailKey[key] = row[key] as string;
          if (getSubTableName.key[key] !== row[key]) {
            openSubTable = false;
          }
        });
        if (
          getSubTableName.name === subTable.subTableColumnName &&
          openSubTable &&
          getSubTableName.subData.length > 0
        ) {
          return (
            <SubTable
              open={
                getSubTableName.name === subTable.subTableColumnName &&
                openSubTable &&
                getSubTableName.subData.length > 0
              }
              subTableColumn={subTable}
              getSubTableName={getSubTableName}
              getSubTableDetail={getSubTableDetail}
              setGetSubTableDetail={setGetSubTableDetail}
              setGetSubTableEditDirect={setGetSubTableEditDirect}
              getSubSubTableName={getSubSubTableName}
              setGetSubSubTableName={setGetSubSubTableName}
              getSubSubTableDetail={getSubSubTableDetail}
              setGetSubSubTableDetail={setGetSubSubTableDetail}
              setGetSubSubTableAdd={setGetSubSubTableAdd}
              setGetSubSubTableEditDirect={setGetSubSubTableEditDirect}
              setTableOriginalFunc={setTableOriginalFunc}
            />
          );
        }
      })}
    </Fragment>
  );
}

interface ConstTableProps {
  getTableData: (edit: boolean) => void;
  tableType: "main" | "sub";
  serchOpen: boolean;
  aiChatOpen?: boolean;
  tableHeight: string;
  tableColumn: TableColumn;
  dataColumn: DataColumn[];
  rows: dataDifine[];
  displayNumber: number;
  setDisplayNumber: (displayNumber: number) => void;
  displayNumbers: number[];
  pageNumber: number;
  setPageNumber: (displayNumber: number) => void;
  maxPageNumber: number;
  totalCount?: number;
  getDataDetail: (key: { [key: string]: string }, edit: boolean) => void;
  getSubTableName: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  };
  setGetSubTableName: (value: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  }) => void;
  getSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  setGetSubTableAdd: (value: { name: string; row: dataDifine | null }) => void;
  setGetSubTableEditDirect: (value: EditDataDifine | null) => void;
  getSubSubTableName: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  };
  setGetSubSubTableName: (value: {
    name: string;
    key: { [key: string]: string };
    subData: subDataDifine[];
  }) => void;
  getSubSubTableDetail: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  };
  setGetSubSubTableDetail: (value: {
    name: string;
    key: { [key: string]: string };
    dataDetail: {
      [key: string]:
        | number
        | string
        | { [key: string]: string | number }[]
        | { [key: string]: string | string[] };
    } | null;
  }) => void;
  setGetSubSubTableAdd: (value: {
    name: string;
    row: dataDifine | null;
  }) => void;
  setGetSubSubTableEditDirect: (value: EditDataDifine | null) => void;
  tableTitle: string;
  switchTableCheckBox: (dataKey: { [key: string]: string }) => void;
  allCheckFlg: boolean;
  switchAllTableCheckBox: () => void;
  editIDs: number[];
  setEditIDs: (value: number[]) => void;
  setEditDatasName: (name: string) => void;
  setGetSubMainTableName: (value: {
    name: string;
    key: { [key: string]: string };
  }) => void;
  setTableOriginalFunc: (value: {
    name: string;
    key: { [key: string]: string };
    subTableColumnName?: string;
  }) => void;
  sortColumn: {
    key: string;
    sort: string;
  }[];
  setSortColumn: (
    value: {
      key: string;
      sort: string;
    }[],
  ) => void;
}

export function ConstTable(props: ConstTableProps) {
  const {
    getTableData,
    tableType,
    serchOpen,
    aiChatOpen = false,
    tableHeight,
    tableColumn,
    dataColumn,
    rows,
    displayNumber,
    setDisplayNumber,
    displayNumbers,
    pageNumber,
    setPageNumber,
    maxPageNumber,
    totalCount,
    getDataDetail,
    getSubTableName,
    setGetSubTableName,
    getSubTableDetail,
    setGetSubTableDetail,
    setGetSubTableAdd,
    setGetSubTableEditDirect,
    getSubSubTableName,
    setGetSubSubTableName,
    getSubSubTableDetail,
    setGetSubSubTableDetail,
    setGetSubSubTableAdd,
    setGetSubSubTableEditDirect,
    tableTitle,
    switchTableCheckBox,
    allCheckFlg,
    switchAllTableCheckBox,
    editIDs,
    setEditIDs,
    setEditDatasName,
    setGetSubMainTableName,
    setTableOriginalFunc,
    sortColumn,
    setSortColumn,
  } = props;

  const loginUser = useAtomValue(AppAtoms.loginUser);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const tableHeaderRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (tableHeaderRef.current && isSmartPhone) {
        tableHeaderRef.current.style.left = `-${window.scrollX}px`;
      }
    };
    window.addEventListener("scroll", handleScroll);

    // クリーンアップ
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const [pageList, setPageList] = useState<any>([]);
  useEffect(() => {
    const pageList = [];
    for (let i = 1; i <= maxPageNumber; i++) {
      pageList.push(
        <MenuItem key={"maxPageNumber" + i} value={i}>
          {i}
        </MenuItem>,
      );
    }
    setPageList(pageList);
  }, [maxPageNumber]);

  const [screenDataColumn, setScreenDataColumn] = useState<DataColumn[]>([]);
  useEffect(() => {
    setScreenDataColumn(
      dataColumn.length > 0 ? dataColumn : tableColumn.dataColumn,
    );
  }, [tableColumn, dataColumn]);

  return (
    <Box
      sx={{
        width: "100%",
        height: tableHeight,
      }}
    >
      {tableType == "main" ? (
        <TableTitle
          serchOpen={serchOpen}
          aiChatOpen={aiChatOpen}
          pageList={pageList}
          tableColumn={tableColumn}
          displayNumber={displayNumber}
          setDisplayNumber={setDisplayNumber}
          displayNumbers={displayNumbers}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          maxPageNumber={maxPageNumber}
          tableTitle={tableTitle}
          getTableData={getTableData}
          totalCount={totalCount}
        />
      ) : null}
      <TableContainer
        component={Paper}
        sx={{
          margin: isSmartPhone
            ? undefined
            : tableType == "main"
              ? "1px 4px"
              : "20px 4px 0px",
          width: isSmartPhone
            ? undefined
            : tableType == "main"
              ? "calc(100% - 8px)"
              : "calc(100% - 8px)",
          height: isSmartPhone
            ? undefined
            : tableType == "main"
              ? "calc(100% - 32px)"
              : "calc(100% - 12px)",
          border: isSmartPhone ? undefined : "1px solid rgb(230, 230, 230)",
          marginTop: isSmartPhone
            ? tableType == "main"
              ? "165px"
              : undefined
            : tableType == "main"
              ? undefined
              : undefined,
          position: isSmartPhone && tableType == "sub" ? "fixed" : undefined,
          bottom: isSmartPhone && tableType == "sub" ? "0px" : undefined,
        }}
      >
        <Table stickyHeader size="small" aria-label="collapsible table">
          <TableHead>
            <TableRow
              ref={tableHeaderRef}
              sx={{
                position:
                  isSmartPhone && tableType == "main" ? "fixed" : undefined,
                top: isSmartPhone && tableType == "main" ? "128px" : undefined,
                zIndex: isSmartPhone && tableType == "main" ? 1 : undefined,
              }}
            >
              {screenDataColumn.map((dataColumn, i) => {
                if (dataColumn.table) {
                  if (dataColumn.key === "contract_number_months") {
                    return getOneYearList().map((yearMonth, year_i) => {
                      return (
                        <TableCell
                          sx={{
                            backgroundColor: CONST.tableBackgroundColor,
                            minWidth: dataColumn.tableMinWidth,
                            maxWidth: isSmartPhone
                              ? dataColumn.tableMinWidth
                              : undefined,
                          }}
                          key={"tableYearMonthColumn" + year_i}
                          align="center"
                        >
                          <Box className="table-header">{yearMonth}</Box>
                        </TableCell>
                      );
                    });
                  } else {
                    if (
                      loginUser &&
                      dataColumn.tableDetailAuth.includes(loginUser.authority)
                    ) {
                      if (dataColumn.tableValue == "edit_check") {
                        return (
                          <TableCell
                            sx={{
                              backgroundColor: CONST.tableBackgroundColor,
                              minWidth: dataColumn.tableMinWidth,
                              maxWidth: isSmartPhone
                                ? dataColumn.tableMinWidth
                                : undefined,
                            }}
                            key={"tableColumn" + i}
                            align="center"
                          >
                            <IconButton
                              disabled={editIDs.length === 0}
                              sx={{
                                height: "25px",
                                width: dataColumn.tableMinWidth,
                              }}
                              aria-label="detail row"
                              size="small"
                              onClick={() => setEditDatasName(dataColumn.name)}
                            >
                              <Box className="table-header">
                                {dataColumn.name}
                              </Box>
                              <EditNoteIcon
                                fontSize="medium"
                                sx={{
                                  "& .MuiSvgIcon-root": {
                                    fontSize: 18,
                                  },
                                  marginLeft: "5px",
                                }}
                              />
                            </IconButton>
                          </TableCell>
                        );
                      } else if (dataColumn.tableValue == "check") {
                        return (
                          <TableCell
                            sx={{
                              backgroundColor: CONST.tableBackgroundColor,
                              minWidth: dataColumn.tableMinWidth,
                              maxWidth: isSmartPhone
                                ? dataColumn.tableMinWidth
                                : undefined,
                            }}
                            key={"tableColumn" + i}
                            align="center"
                          >
                            <Box className="table-header">
                              {dataColumn.name}
                              <IconButton
                                disabled={rows.length === 0}
                                sx={{
                                  height: "25px",
                                  width: "25px",
                                  marginTop: "-5px",
                                }}
                                aria-label="detail row"
                                size="small"
                                onClick={switchAllTableCheckBox}
                              >
                                <Checkbox
                                  disabled={rows.length === 0}
                                  checked={
                                    rows.length === 0 ? false : allCheckFlg
                                  }
                                  sx={{
                                    "& .MuiSvgIcon-root": { fontSize: 18 },
                                  }}
                                />
                              </IconButton>
                            </Box>
                          </TableCell>
                        );
                      } else {
                        return (
                          <TableCell
                            sx={{
                              backgroundColor: CONST.tableBackgroundColor,
                              minWidth: dataColumn.tableMinWidth,
                              maxWidth: isSmartPhone
                                ? dataColumn.tableMinWidth
                                : undefined,
                            }}
                            key={"tableColumn" + i}
                            align="center"
                          >
                            <Box className="table-header">
                              {dataColumn.name}
                              {dataColumn.sort ? (
                                <IconButton
                                  sx={{
                                    height: "25px",
                                    width: "25px",
                                    marginTop: "-5px",
                                  }}
                                  aria-label="detail row"
                                  size="small"
                                  onClick={() => {
                                    const sort = sortColumn.some(
                                      (criterion) =>
                                        criterion.key === dataColumn.key &&
                                        criterion.sort === "DESC",
                                    )
                                      ? "ASC"
                                      : "DESC";
                                    let _sortColumn = sortColumn.concat();
                                    _sortColumn = _sortColumn.filter(
                                      (column) => {
                                        return column.key !== dataColumn.key;
                                      },
                                    );
                                    _sortColumn.unshift({
                                      key: dataColumn.key,
                                      sort,
                                    });
                                    setSortColumn(_sortColumn);
                                  }}
                                >
                                  {sortColumn.some(
                                    (criterion) =>
                                      criterion.key === dataColumn.key &&
                                      criterion.sort === "DESC",
                                  ) ? (
                                    <ArrowDropDownIcon
                                      sx={{
                                        "& .MuiSvgIcon-root": { fontSize: 18 },
                                      }}
                                    />
                                  ) : (
                                    <ArrowDropUpIcon
                                      sx={{
                                        "& .MuiSvgIcon-root": { fontSize: 18 },
                                      }}
                                    />
                                  )}
                                </IconButton>
                              ) : null}
                            </Box>
                          </TableCell>
                        );
                      }
                    }
                  }
                }
              })}
              {tableColumn.subTable.map((subTable, subTable_i) => {
                if (
                  loginUser &&
                  subTable.subTableAuth.includes(loginUser.authority)
                ) {
                  return (
                    <>
                      <TableCell
                        sx={{
                          backgroundColor: CONST.tableBackgroundColor,
                          minWidth: subTable.subTableColumnNameWidth,
                          maxWidth: isSmartPhone
                            ? subTable.subTableColumnNameWidth
                            : undefined,
                        }}
                        key={"tableColumnSub" + subTable_i}
                        align="center"
                      >
                        <Box className="table-header">
                          {subTable.subTableColumnName}
                        </Box>
                      </TableCell>
                      {subTable.subTableAdd ? (
                        <TableCell
                          sx={{
                            backgroundColor: CONST.tableBackgroundColor,
                            minWidth: subTable.subTableAddTitleWidth,
                            maxWidth: isSmartPhone
                              ? subTable.subTableAddTitleWidth
                              : undefined,
                          }}
                          key={"tableColumnSubAdd" + subTable_i}
                          align="center"
                        >
                          <Box className="table-header">
                            {subTable.subTableAddTitle}
                          </Box>
                        </TableCell>
                      ) : null}
                      {subTable.mainTableDetail ? (
                        <TableCell
                          sx={{
                            backgroundColor: CONST.tableBackgroundColor,
                            minWidth: "140px",
                            maxWidth: isSmartPhone ? "140px" : undefined,
                          }}
                          key="tableColumnDetail"
                          align="center"
                        >
                          <Box className="table-header">
                            {subTable.mainTableDetailColumn}
                          </Box>
                        </TableCell>
                      ) : null}
                    </>
                  );
                }
              })}
              {tableColumn.detail ? (
                <TableCell
                  sx={{
                    backgroundColor: CONST.tableBackgroundColor,
                    minWidth: "60px",
                    maxWidth: isSmartPhone ? "60px" : undefined,
                  }}
                  key="tableColumnDetail"
                  align="center"
                >
                  <Box className="table-header">{tableColumn.detailColumn}</Box>
                </TableCell>
              ) : null}

              {tableColumn.tableFunction.map((tableFunction, i) => {
                if (
                  loginUser &&
                  tableFunction.auth.includes(loginUser.authority)
                ) {
                  return (
                    <TableCell
                      sx={{
                        backgroundColor: CONST.tableBackgroundColor,
                        minWidth: tableFunction.width,
                        maxWidth: isSmartPhone
                          ? tableFunction.width
                          : undefined,
                      }}
                      key={"tableColumnFunc" + i}
                      align="center"
                    >
                      <Box className="table-header">{tableFunction.name}</Box>
                    </TableCell>
                  );
                }
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <Row
                key={"row" + index}
                row={row}
                index={index}
                tableColumn={tableColumn}
                screenDataColumn={screenDataColumn}
                getDataDetail={getDataDetail}
                getSubTableName={getSubTableName}
                setGetSubTableName={setGetSubTableName}
                getSubTableDetail={getSubTableDetail}
                setGetSubTableDetail={setGetSubTableDetail}
                setGetSubTableAdd={setGetSubTableAdd}
                setGetSubTableEditDirect={setGetSubTableEditDirect}
                getSubSubTableName={getSubSubTableName}
                setGetSubSubTableName={setGetSubSubTableName}
                getSubSubTableDetail={getSubSubTableDetail}
                setGetSubSubTableDetail={setGetSubSubTableDetail}
                setGetSubSubTableAdd={setGetSubSubTableAdd}
                setGetSubSubTableEditDirect={setGetSubSubTableEditDirect}
                switchTableCheckBox={switchTableCheckBox}
                editIDs={editIDs}
                setEditIDs={setEditIDs}
                setGetSubMainTableName={setGetSubMainTableName}
                setTableOriginalFunc={setTableOriginalFunc}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
